<template>
  <div>
    <EmptyState
      v-if="!loading && totalRecords === 0"
      class="pt-8 pb-12 px-6"
      :icon="'mdi-beehive-outline'"
      :iconSize="36"
      :title="emptyStateMessage"
    />
    <v-data-table
      v-else
      data-private
      disable-sort
      calculate-widths
      :headers="headers"
      :footer-props="footerOptions"
      :items="rows"
      :options.sync="tableOptions"
      :server-items-length="totalRecords"
      :loading="loading"
      :mobile-breakpoint="$vuetify.breakpoint.thresholds.md"
      :no-data-text="$t('home.teams_activity.findings_tab_empty')"
      @click:row="$emit('showFinding', $event.id)"
    >
      <template v-slot:item.name="{ item }">
        <v-tooltip top max-width="300px">
          <template v-slot:activator="{ on }">
            <span v-on="on" v-snip="1" class="body-2 primary--text">{{
              item.name
            }}</span>
          </template>
          {{ item.name }}
        </v-tooltip>
      </template>

      <template v-slot:item.status="{ item }">
        <FindingStatus :status="item.status" />
      </template>

      <template v-slot:item.findingCreatedDate="{ item }">
        <span class="body-2 blue-grey--text text--darken-3">
          {{ item.findingCreatedDate }}
        </span>
      </template>

      <template v-slot:item.routineExecution="{ item }">
        <v-tooltip top max-width="300px">
          <template v-slot:activator="{ on }">
            <span
              v-on="on"
              v-snip="1"
              class="body-2 blue-grey--text text--darken-3"
            >
              {{ item.routineExecution }}</span
            >
          </template>
          {{ item.routineExecution }}
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import api from '@/api'
import { utils } from 'frontend-js-shared'
import mixin from '@/mixins/mixin.vue'

import FindingStatus from '@/components/FindingDrawer/FindingStatus.vue'
import EmptyState from '@/components/EmptyState.vue'

const INITIAL_ITEMS_PER_PAGE = 20

export default {
  name: 'FindingsSection',

  props: {
    organizationId: {
      required: true,
      type: String,
    },
    accountId: {
      required: true,
      type: String,
    },
    filterSelected: {
      type: String,
      default: 'CREATED_BY_ME',
    },
  },

  mixins: [mixin],

  components: {
    FindingStatus,
    EmptyState,
  },

  data() {
    return {
      findings: [],
      loading: true,
      totalRecords: 0,
      lastRequestKey: null,
      requestSequence: 0,
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      headers: [
        {
          text: this.$t('common.name'),
          value: 'name',
          show: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
          show: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('filters.date.creation_date'),
          value: 'findingCreatedDate',
          show: true,
          cellClass: 'cursor-pointer',
        },
        {
          text: this.$t('routines.routine_execution'),
          value: 'routineExecution',
          show: true,
          cellClass: 'cursor-pointer',
        },
      ],
    }
  },

  methods: {
    async fetchFindings(options) {
      if (!this.accountId) return

      const requestParams = {
        organizationId: this.organizationId,
        filters: this.filterType[this.filterSelected],
        sort: {
          by: 'createdAt',
          order: 'desc',
        },
        limit: options.itemsPerPage,
        skip: options.itemsPerPage * (options.page - 1),
      }

      const requestKey = JSON.stringify(requestParams)
      if (requestKey === this.lastRequestKey) return
      this.lastRequestKey = requestKey
      this.requestSequence += 1
      const requestId = this.requestSequence

      try {
        this.loading = true
        const { data } = await api.findings.getAll(requestParams)

        if (requestId !== this.requestSequence) return

        this.findings = data.results ?? []
        this.totalRecords = data.totalRecordsInData ?? 0
      } catch (err) {
        if (requestId !== this.requestSequence) return
        this.lastRequestKey = null
        this.handleRequestError(err)
      } finally {
        if (requestId === this.requestSequence) this.loading = false
      }
    },
    onFindingDrawerToggle(findingId) {
      this.isOpenFindingDrawer = true
      this.selectedFindingId = findingId
    },
  },

  computed: {
    rows() {
      return this.findings.map(finding => {
        return {
          id: finding.id,
          name: finding.situation,
          status: finding.status,
          findingCreatedDate: finding.createdAt
            ? utils.dates.formatDate(finding.createdAt, 'MMM D, YYYY, h:mm A')
            : '',
          routineExecution: finding.routine?.name,
        }
      })
    },

    tableOptions: {
      get() {
        return {
          itemsLength: this.totalRecords,
          itemsPerPage: INITIAL_ITEMS_PER_PAGE,
          page: 1,
        }
      },

      set(event) {
        this.fetchFindings({
          itemsLength: this.totalRecords,
          itemsPerPage: event.itemsPerPage,
          page: event.page,
        })
      },
    },

    filterType() {
      return {
        CREATED_BY_ME: { isDeleted: false, createdBy: this.accountId },
        ASSIGNED_TO_ME: { isDeleted: false, assignees: [this.accountId] },
      }
    },

    emptyStateMessage() {
      return this.isFindingsTab
        ? this.$t('routines.profile_findings_empty')
        : this.$t('routines.executions_empty')
    },
  },

  watch: {
    accountId: {
      deep: true,
      handler() {
        this.fetchFindings(this.tableOptions)
      },
    },
    filterSelected(newVal, oldVal) {
      if (oldVal === newVal) return
      this.fetchFindings(this.tableOptions)
    },
  },
}
</script>
